import { useWatched } from './DiscoveryPreferences';
import Carousel from "react-bootstrap/Carousel";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FiArrowRight, FiPause, FiPlay, FiVolumeX } from "react-icons/fi";
import axios from "axios";
import Api_key from "./apiconfiguration";
import LoadingScreen from "./LoadingScreen";
import preloadImage from "./preloadImage";
import { WatchlistButton } from "./LibraryActions";
import useArtworkTheme from './useArtworkTheme';
import TitleLogo from './TitleLogo';

const CarouselComponent = ({selection}) => {
  const [allMovies, setMovies] = useState([]);
  const {watched,hideWatched} = useWatched();
  const movies = (selection || allMovies).filter(movie => !hideWatched || !watched.has(`movie_${movie.id}`));
  const [activeIndex, setActiveIndex] = useState(0);
  const selectionId = selection?.[0]?.id;
  useEffect(() => setActiveIndex(0),[selectionId]);
  useEffect(() => { setActiveIndex(index => Math.min(index,Math.max(0,movies.length-1))); },[movies.length]);
  useArtworkTheme(movies[activeIndex]?.backdrop_path || movies[activeIndex]?.poster_path);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trailerKey, setTrailerKey] = useState(null);
  const hoverTimer = useRef(null);
  const trailerCache = useRef(new Map());
  const navigate = useNavigate();
  const [paused, setPaused] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  useEffect(() => {
    axios.get(`https://api.themoviedb.org/3/movie/popular?api_key=${Api_key}`)
      .then(async ({ data }) => {
        const featured = data.results.slice(0, 7);
        await preloadImage(`https://image.tmdb.org/t/p/w1280${featured[0]?.backdrop_path || ""}`);
        setMovies(featured);
        featured.slice(1).forEach((movie) =>
          preloadImage(`https://image.tmdb.org/t/p/w1280${movie.backdrop_path || ""}`)
        );
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const previewMovie = (id) => {
    if (paused || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(async () => {
      if (trailerCache.current.has(id)) return setTrailerKey(trailerCache.current.get(id));
      try {
        const { data } = await axios.get(`https://api.themoviedb.org/3/movie/${id}/videos?api_key=${Api_key}`);
        const trailer = data.results.find(video => video.site === 'YouTube' && video.type === 'Trailer');
        trailerCache.current.set(id, trailer?.key || null);
        setTrailerKey(trailer?.key || null);
      } catch { setTrailerKey(null); }
    }, 700);
  };

  const stopPreview = () => { clearTimeout(hoverTimer.current); setTrailerKey(null); };
  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  if (loading) return <LoadingScreen compact label="Preparing tonight’s highlights" />;
  if (error) return <div className="hero-error">Unable to load highlights.</div>;

  if (!movies.length) return <div className="hero-error">You have watched these spotlight titles. Discover more below.</div>;
  return (
    <div className="spotlight-shell" style={{ position: 'relative' }}>
    <Carousel fade activeIndex={activeIndex} onSelect={setActiveIndex} interval={paused ? null : 6500} onSlide={stopPreview} pause="hover" controls={false} className="backdrop-carousel">
      {movies.map((movie, index) => (
        <Carousel.Item key={movie.id} onMouseEnter={() => {if(window.matchMedia('(hover: hover) and (min-width: 701px)').matches) previewMovie(movie.id);}} onMouseLeave={stopPreview}>
          <div className="hero-backdrop" style={{ backgroundImage: `url(https://image.tmdb.org/t/p/w1280${movie.backdrop_path})` }} />
          {trailerKey && <div className="hero-trailer"><iframe src={`https://www.youtube-nocookie.com/embed/${trailerKey}?autoplay=1&mute=1&controls=0&loop=1&playlist=${trailerKey}&modestbranding=1`} title={`${movie.title} trailer`} allow="autoplay; encrypted-media" /><span><FiVolumeX /> Preview</span></div>}
          <Carousel.Caption>
            <span className="hero-eyebrow">HEDONE SPOTLIGHT <i>#{index + 1} TRENDING</i></span>
            <TitleLogo id={movie.id} title={movie.title} enabled={Math.abs(index - activeIndex) <= 1} />
            <div className="hero-meta"><strong>{movie.release_date?.slice(0, 4)}</strong><span>{movie.vote_average?.toFixed(1)} score</span><span className="quality-badge">MOVIE</span></div>
            <p>{movie.overview}</p>
            <div className="hero-actions">
              <button className="hero-primary" onClick={() => navigate(`/movie/${movie.id}`)}>Explore movie<FiArrowRight /></button>
              <WatchlistButton item={movie} mediaType="movie" compact />

            </div>
          </Carousel.Caption>
        </Carousel.Item>
      ))}
    </Carousel>
    <button className="hero-playback" onClick={() => { stopPreview(); setPaused(value => !value); }} aria-label={paused ? 'Play spotlight slideshow' : 'Pause spotlight slideshow'}>{paused ? <FiPlay /> : <FiPause />}</button>
    </div>
  );
};
export default CarouselComponent;
